import { LoyaltyMinTierPoints, LoyaltyTier } from '@montugroup/contracts-warehousing-api';
import { INITIAL_TIER_SEGMENT_SIZE, LOYALTY_TRANSACTIONS_CYCLE_DAYS, loyaltyTiers } from '~/constants';
import type { LoyaltyTierImage } from '~/types';
import { resolveISODateToAEDTAEST } from './resolveDateToTimezone';

/**
 * Get the name of a loyalty tier based on its tier value
 * e.g. 0 = 'None', 10 = 'Bronze', 20 = 'Silver', 30 = 'Gold', 40 = 'Platinum'
 */
export const getLoyaltyTierName = (tierValue: number | undefined): string => {
  const tierEntries = Object.entries(LoyaltyTier);

  // Retrieve tier names
  const tierName = tierEntries.find(([_, value]) => value === tierValue)?.[0];

  return tierName || 'None';
};

/**
 * Map a loyalty tier to its respective image
 */
export const tierImages: LoyaltyTierImage = {
  [LoyaltyTier.Bronze]: '/loyalty-tier-bronze.png',
  [LoyaltyTier.Silver]: '/loyalty-tier-silver.png',
  [LoyaltyTier.Gold]: '/loyalty-tier-gold.png',
  [LoyaltyTier.Platinum]: '/loyalty-tier-platinum.png',
  [LoyaltyTier.None]: '/loyalty-tier-none.png',
};

/**
 * Get the next loyalty tier info
 * @param tier - current loyalty tier
 * @returns next loyalty tier info or null if current tier is the highest tier
 */
export const getNextLoyaltyTier = (tier: LoyaltyTier) => {
  const tierIndex = loyaltyTiers.findIndex((t) => t.tier === tier);
  const nextTier = loyaltyTiers[tierIndex + 1]?.tier;
  const nextTierName = getLoyaltyTierName(nextTier);
  const nextTierPoints = loyaltyTiers[tierIndex + 1]?.minPoints;

  if (!nextTier) {
    return null;
  }

  return { tier: nextTier, name: nextTierName, minPoints: nextTierPoints, image: tierImages[nextTier] };
};

/**
 * Get the loyalty tier info using the tier ID
 * @param tier - 0, 10, 20, 30, 40
 */
export const getLoyaltyTier = (tier: LoyaltyTier) => {
  const currentTier = loyaltyTiers.find((t) => t.tier === tier);

  if (!currentTier) {
    return null;
  }

  return {
    image: tierImages[currentTier.tier],
    minPoints: currentTier.minPoints,
    name: getLoyaltyTierName(currentTier.tier),
    tier: currentTier.tier,
    nextTier: getNextLoyaltyTier(currentTier.tier),
  };
};

/**
 * Map over the loyalty tiers and construct additional properties for each tier (such as next tier, min/max points, etc)
 */
export const getLoyaltyTiers = () => {
  return loyaltyTiers.map((tier) => {
    const nextTier = getNextLoyaltyTier(tier.tier);

    return {
      tier: tier.tier,
      name: getLoyaltyTierName(tier.tier),
      minPoints: tier.minPoints,
      maxPoints: nextTier?.minPoints ?? LoyaltyMinTierPoints.Platinum,
      image: tierImages[tier.tier],
      nextTier: getNextLoyaltyTier(tier.tier),
    };
  });
};

/**
 * Calculate the position (percentage) where each tier circle should be placed on the progress bar
 */
export const getProgressBarTierPosition = (tier: LoyaltyTier): number => {
  // The None tier is always at the start of the progress bar
  if (tier === LoyaltyTier.None) {
    return 0;
  }

  const tiers = getLoyaltyTiers().filter((t) => t.tier !== LoyaltyTier.None);
  const tierIndex = tiers.findIndex((t) => t.tier === tier);

  // Calculate the position of the tier, excluding the initial tier segment (since it has its own fixed size)
  const position = tierIndex / (tiers.length - 1);

  return position * (100 - INITIAL_TIER_SEGMENT_SIZE) + INITIAL_TIER_SEGMENT_SIZE;
};

/**
 * Calculate the progress of a loyalty tier based on the points balance
 */
export const calculateTierProgress = (pointsBalance: number): number => {
  if (pointsBalance >= LoyaltyMinTierPoints.Platinum) {
    return 100;
  }

  const tiers = getLoyaltyTiers();
  const currentTier = tiers.find((tier) => pointsBalance >= tier.minPoints && pointsBalance < tier.maxPoints);

  if (!currentTier) {
    return 0;
  }

  // Calculate progress within the current tier segment
  const segmentProgress = (pointsBalance - currentTier.minPoints) / (currentTier.maxPoints - currentTier.minPoints);

  // Get the start and end positions for the current tier
  const currentPosition = getProgressBarTierPosition(currentTier.tier);
  const nextTier = currentTier.nextTier;
  const nextPosition = nextTier ? getProgressBarTierPosition(nextTier.tier) : 100;

  // Calculate the current tier's progress by interpolating between the current and next tier positions
  return currentPosition + segmentProgress * (nextPosition - currentPosition);
};

/**
 * Map loyalty tiers to their respective gradient colors
 * Each tier has a start and end color for the gradient
 */
export const loyaltyTierGradients: Omit<Record<LoyaltyTier, { start: string; end: string }>, LoyaltyTier.None> = {
  [LoyaltyTier.Bronze]: { start: '#C4813C', end: '#462E16' },
  [LoyaltyTier.Silver]: { start: '#A3A3A3', end: '#3D3D3D' },
  [LoyaltyTier.Gold]: { start: '#FFD27D', end: '#8A6F35' },
  [LoyaltyTier.Platinum]: { start: '#E9EAE9', end: '#838483' },
};

export const getLoyaltyCycleLastDay = (cycleResetDate: string) =>
  resolveISODateToAEDTAEST(cycleResetDate)
    .startOf('day')
    .plus({
      // We subtract 1 day from the cycle because the cycle is inclusive of the start date
      days: LOYALTY_TRANSACTIONS_CYCLE_DAYS - 1,
    });

export const getLoyaltyCycleFirstDayFromCreditExpirationTransaction = (cycleResetDate: string) =>
  // We subtract 1 because the cycle is inclusive of the end date
  resolveISODateToAEDTAEST(cycleResetDate)
    .startOf('day')
    .minus({ days: LOYALTY_TRANSACTIONS_CYCLE_DAYS - 1 });
